import { useEffect, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Printer, Trash2 } from 'lucide-react';
import { PRINT_LOGO_WIDTH_DOTS, type PrintLogoStatus } from '@gc/shared';

import { api, ApiError } from '@/lib/api';
import { convertLogoFile } from './printLogo';

/**
 * The logo that prints on receipts.
 *
 * Separate from the venue logo above it, and that is not an oversight. The venue
 * logo is a colour image for screens; a thermal printer has no colour and no
 * greys — it burns a dot or it does not. The two want different artwork, and a
 * venue whose screen logo is white-on-transparent would print a blank rectangle
 * if the same file were reused.
 *
 * The conversion happens HERE, in the browser, at the moment of upload: decode,
 * scale to the paper, threshold, pack to bits. The server stores the result. It
 * cannot do the work itself without an image library, and doing it per receipt
 * would put image processing between a cashier pressing Settle and paper coming
 * out.
 */
export function PrintLogoField() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<PrintLogoStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<PrintLogoStatus>('/settings/print-logo')
      .then(setStatus)
      .catch(() => setStatus({ configured: false, heightDots: null }));
  }, []);

  const upload = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    // Cleared immediately so choosing the SAME file twice still fires a change
    // — the ordinary way somebody retries after a failed conversion.
    event.target.value = '';
    if (!file) return;

    setBusy(true);
    setError(null);
    try {
      const logo = await convertLogoFile(file);
      setStatus(await api.put<PrintLogoStatus>('/settings/print-logo', logo));
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : caught instanceof Error
            ? caught.message
            : t('settings.general.printLogo.failed'),
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setStatus(await api.delete<PrintLogoStatus>('/settings/print-logo'));
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : t('settings.general.printLogo.failed'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-print-logo className="rounded-card border border-subtle bg-raised p-4">
      <div className="mb-1 flex items-center gap-2">
        <Printer className="h-4 w-4 text-content-secondary" aria-hidden />
        <span className="text-sm font-semibold text-content">
          {t('settings.general.printLogo.title')}
        </span>
      </div>
      <p className="mb-3 text-xs text-content-secondary">
        {t('settings.general.printLogo.hint', { width: PRINT_LOGO_WIDTH_DOTS })}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex min-h-9 cursor-pointer items-center rounded-control border border-subtle bg-input px-3 text-sm text-content hover:bg-hover">
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(event) => void upload(event)}
            disabled={busy}
            className="hidden"
          />
          {t('settings.general.printLogo.choose')}
        </label>

        {status?.configured ? (
          <>
            <span data-print-logo-set className="text-xs text-content-secondary">
              {t('settings.general.printLogo.set', { height: status.heightDots })}
            </span>
            <button
              type="button"
              onClick={() => void remove()}
              disabled={busy}
              className="inline-flex min-h-9 items-center gap-1.5 rounded-control border border-subtle px-3 text-xs text-content-secondary hover:border-danger/60 hover:text-danger disabled:opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              {t('common.delete')}
            </button>
          </>
        ) : (
          <span className="text-xs text-content-muted">{t('settings.general.printLogo.none')}</span>
        )}

        {busy ? <span className="text-xs text-content-muted">{t('common.loading')}</span> : null}
      </div>

      {error ? (
        <p role="alert" className="mt-2 text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
